import React, {
  FormEventHandler,
  useState,
  ChangeEvent,
  ChangeEventHandler,
  Fragment,
} from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Container,
} from 'reactstrap';
import { Store } from 'react-notifications-component';
import countryList from 'country-list';
import 'react-datepicker/dist/react-datepicker.css';
import FormTextInput from '../../../components/FormTextInput';
import FormSelectInput from '../../../components/FormSelectInput';
import ModalWindow from '../../../components/modalWindow';
import {
  useGetSpecialityListRequest,
  useGetPlansRequest,
  useCreateRegistrationLinkRequest,
} from '../../../api/registerLink';
import { DateDay } from '@integraciones_doc24/wehealthy-api/types/common/date';
import { format } from 'date-fns';
import { DestinationType, useDestination } from '../../../hooks/useDestination';
import FechaNacimiento from './FechaNacimiento';
import Genero from './Genero';
import {FormErrors, FormValues} from '../../../types/general-types'

type ResponseData = {
  id: string;
  label: string;
};

const baseFields = {
  firstName: '',
  lastName: '',
  documentNumber: '',
  phone: '',
  email: '',
  countryId: '',
} as const;

const fields = {
  ...baseFields,
  gender: 'm',
  birthday: null,
} as const;

const errors = {
  ...baseFields,
  gender: '',
  birthday: '',
} as const;

const NuevoLinkRegistro = () => {
  const [formValues, setFormValues] = useState<FormValues>(fields);
  const [formErrors, setFormErrors] = useState<FormErrors>(errors);
  const [destinoEstablecido, setDestinoEstablecido] = useState<boolean>(false);
  const [tipoDestino, setTipoDestino] = useState<DestinationType>();
  const [especialidades, setEspecialidades] = useState<ResponseData[] | null>(
    null,
  );
  const [especialidadSeleccionada, setEspecialidadesSeleccionada] =
    useState<number>();
  const [formaPagoSeleccionada, setFormaPagoSeleccionada] = useState<string>();
  const [planes, setPlanes] = useState<ResponseData[] | null>(null);
  const [planSeleccionado, setPlanSeleccionado] = useState('');
  const [visibilidadModal, setVisibilidadModal] = useState<boolean>(false);
  const [contenidoModal, setContenidoModal] = useState<string>('');
  const [textoACopiar, setTextoACopiar] = useState<string>('');

  const paises = countryList.getData();
  const mappedCountries = paises.map((c) => ({ id: c.code, label: c.name }));
  const tiposDestinos = [
    { id: 'instantVideo', label: 'Consulta Inmediata' },
    { id: 'videoAppointment', label: 'Agendar Consulta' },
    { id: 'plan', label: 'Suscribirse a un plan' },
  ];
  const formasDePago = [
    { id: 'pagoUnico', label: 'Pago Único' },
    { id: 'suscribirPlan', label: 'Subscribir a un plan' },
  ];

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((formValues) => ({
      ...formValues,
      [name]: value,
    }));
    setFormErrors((formErrors) => ({
      ...formErrors,
      [name]: '',
    }));
  };

  const createRegistrationLink = useCreateRegistrationLinkRequest();
  const getDestination = useDestination();

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    let tieneErrores = false;
    const errores: FormErrors = {};
    Object.entries(formValues).forEach(([clave, valor]) => {
      if (!valor) {
        tieneErrores = true;
        errores[clave as keyof FormValues] =
          `El campo ${clave} es obligatorio` as string;
      }
    });
    if (tieneErrores) {
      const nuevosErrores = { ...formErrors };
      Object.entries(formValues).forEach(([clave, valor]) => {
        if (!valor) {
          nuevosErrores[clave as keyof FormValues] =
            `El campo ${clave} es obligatorio` as string;
        }
      });
      setFormErrors(nuevosErrores);
    } else {
      if (!formValues.birthday) {
        formValues.birthday = new Date();
      }

      const birthdayDate =
        typeof formValues.birthday === 'string'
          ? new Date(formValues.birthday)
          : formValues.birthday;
      const birthday = format(birthdayDate, 'yyyy-MM-dd').toString() as DateDay;
      const destination = tipoDestino
        ? getDestination({
          type: tipoDestino,
          speciality: especialidadSeleccionada,
          planId: planSeleccionado,
        })
        : undefined;

      const link = await createRegistrationLink(
        { ...formValues, birthday },
        destination,
      );

      setVisibilidadModal(true);
      setContenidoModal(`<a href="${link}" target="_blank">${link}</a>`);
      setTextoACopiar(link);
    }
  };

  const getSpecialityList = useGetSpecialityListRequest();
  const getPlans = useGetPlansRequest();

  const handleTipoDestinoChange: ChangeEventHandler<
    HTMLInputElement
  > = async (e: { target: { value: React.SetStateAction<string> } }) => {
    let tipoDestino = e.target.value as DestinationType;
    setTipoDestino(tipoDestino);
    if (tipoDestino === 'instantVideo' || tipoDestino === 'videoAppointment') {
      setPlanes(null);
      const specialities = await getSpecialityList(tipoDestino);
      const mappedSpecialities = specialities.map((c) => ({
        id: c.id.toString(),
        label: c.description,
      }));
      setEspecialidades(mappedSpecialities);
    } else {
      if (tipoDestino === 'plan') {
        setEspecialidades(null);
        const plans = await getPlans();
        const mappedPlanes = plans.map((c) => ({
          id: c.id.toString(),
          label: c.name,
        }));
        setPlanes(mappedPlanes);
      }
    }
  };

  const handleFormaDePago: ChangeEventHandler<HTMLInputElement> = async (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    let formaDePago = (e.target.value as string) ?? undefined;
    setFormaPagoSeleccionada(formaDePago);
    if (formaDePago === 'suscribirPlan') {
      const plans = await getPlans();
      const mappedPlanes = plans.map((c) => ({
        id: c.id.toString(),
        label: c.name,
      }));
      setPlanes(mappedPlanes);
    }
  };

  const actionForModal = async () => {
    try {
      await navigator.clipboard.writeText(textoACopiar);
      Store.addNotification({
        title: 'Excelente',
        message: 'El texto fue copiado a tu portapapeles',
        type: 'success',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 5000,
          onScreen: true,
        },
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const campos:any = [
    { id: 'firstName', label: 'Nombre', value: formValues.firstName, evento: handleInputChange, error: formErrors.firstName },
    { id: 'lastName', label: 'Apellido', value: formValues.lastName, evento: handleInputChange, error: formErrors.lastName },
    { id: 'documentNumber', label: 'DNI', value: formValues.documentNumber, evento: handleInputChange },
    { id: 'phone', label: 'Teléfono', value: formValues.phone, evento: handleInputChange, error: formErrors.phone },
    { id: 'email', label: 'Correo electrónico', value: formValues.email, evento: handleInputChange, error: formErrors.email },
  ];

  return (
    <Container>
      <Row>
        <Col>
          <h2 className="mt-4">Crear Enlace de registro</h2>
          <ModalWindow
            estadoVisibilidad={visibilidadModal}
            cambiarVisibilidad={setVisibilidadModal}
            onClose={() => {
              setFormValues(fields);
              setDestinoEstablecido(false);
              setTipoDestino(undefined);
              setEspecialidadesSeleccionada(undefined);
              setFormaPagoSeleccionada(undefined);
            }}
            contenidoModal={contenidoModal}
            titulo="Resultado:"
            actionForModal={actionForModal}
            labelForAction="Copiar"
          />
          <Form onSubmit={handleSubmit}>
            {campos.map((field:any) => (
              <FormTextInput key={field.id} {...field} />
            ))}
            <FechaNacimiento formErrors={formErrors} formValues={formValues} setFormErrors={setFormErrors} setFormValues={setFormValues} />
            <Genero formErrors={formErrors} handleInputChange={handleInputChange} />
            <FormSelectInput
              label="País"
              id="countryId"
              options={mappedCountries}
              evento={handleInputChange}
              error={formErrors.countryId}
              placeholder="Seleccione un país"
            />
            <Row>
              <Col md={{ size: 11, offset: 1 }}>
                <FormGroup check className="mt-5">
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={destinoEstablecido}
                      onChange={(e) => {
                        setDestinoEstablecido(e.target.checked);
                      }}
                    />{' '}
                    Establecer destino
                  </Label>
                </FormGroup>
              </Col>
              {destinoEstablecido && (
                <Col md={{ size: 11, offset: 1 }} className="mt-2">
                  <FormSelectInput
                    label="Tipo de destino"
                    id="tipoDestino"
                    options={tiposDestinos}
                    evento={handleTipoDestinoChange}
                    error={undefined}
                    placeholder=" "
                  />
                  {tipoDestino && especialidades && (
                    <Fragment>
                      <FormSelectInput
                        label="Especialidades"
                        id="especialidades"
                        options={especialidades}
                        evento={(e) => {
                          setEspecialidadesSeleccionada(Number(e.target.value));
                        }}
                        error={undefined}
                      />
                      <FormSelectInput
                        label="Forma de Pago"
                        id="formadePago"
                        options={formasDePago}
                        placeholder=" "
                        evento={(e) => {
                          handleFormaDePago(e);
                        }}
                        error={undefined}
                      />
                    </Fragment>
                  )}
                  {!!planes?.length &&
                    (tipoDestino === 'plan' ||
                      formaPagoSeleccionada === 'suscribirPlan') && (
                      <Fragment>
                        <FormSelectInput
                          label="Planes"
                          id="planes"
                          options={planes}
                          placeholder=" "
                          evento={(e) => {
                            setPlanSeleccionado(e.target.value);
                          }}
                          error={undefined}
                        />
                      </Fragment>
                    )}
                </Col>
              )}
            </Row>
            <Button type="submit">Enviar</Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default NuevoLinkRegistro;
